import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { collection, addDoc, updateDoc, deleteDoc, doc, onSnapshot, query, orderBy } from 'firebase/firestore';
import { db } from '../firebase';
import './SavedAddressesPage.css';

const LABEL_OPTIONS = [
    {
        value: 'home', label: 'Home',
        icon: (
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M3 9l9-7 9 7v11a2 2 0 01-2 2H5a2 2 0 01-2-2z" /><polyline points="9 22 9 12 15 12 15 22" />
            </svg>
        ),
    },
    {
        value: 'work', label: 'Work',
        icon: (
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <rect x="2" y="7" width="20" height="14" rx="2" /><path d="M16 7V5a2 2 0 00-2-2h-4a2 2 0 00-2 2v2" />
            </svg>
        ),
    },
    {
        value: 'other', label: 'Other',
        icon: (
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M21 10c0 7-9 13-9 13S3 17 3 10a9 9 0 0118 0z" /><circle cx="12" cy="10" r="3" />
            </svg>
        ),
    },
];

const FILTER_OPTIONS = [
    { value: 'all', label: 'All' },
    { value: 'home', label: 'Home' },
    { value: 'work', label: 'Work' },
    { value: 'other', label: 'Other' },
];

const emptyAddress = {
    label: 'home',
    flatNo: '',
    street: '',
    landmark: '',
    city: '',
    state: '',
    pincode: '',
    isDefault: false,
};

const emptyErrors = { street: '', city: '', pincode: '' };

const SavedAddressesPage = () => {
    const { currentUser } = useAuth();
    const [addresses, setAddresses] = useState([]);
    const [loading, setLoading] = useState(true);
    const [showForm, setShowForm] = useState(false);
    const [editMode, setEditMode] = useState(false);
    const [editId, setEditId] = useState(null);
    const [form, setForm] = useState({ ...emptyAddress });
    const [errors, setErrors] = useState({ ...emptyErrors });
    const [saving, setSaving] = useState(false);
    const [deletingId, setDeletingId] = useState(null);
    const [confirmDeleteId, setConfirmDeleteId] = useState(null);
    const [toast, setToast] = useState({ msg: '', type: 'success' });
    const [filter, setFilter] = useState('all');
    const [copiedId, setCopiedId] = useState(null);
    const [geoStatus, setGeoStatus] = useState('');
    const firstInputRef = useRef(null);

    const handleFetchLocation = () => {
        if (!navigator.geolocation) { setGeoStatus('error'); return; }
        setGeoStatus('loading');
        navigator.geolocation.getCurrentPosition(
            async (pos) => {
                const { latitude, longitude } = pos.coords;
                try {
                    const res = await fetch(
                        `https://nominatim.openstreetmap.org/reverse?lat=${latitude}&lon=${longitude}&format=json`,
                        { headers: { 'Accept-Language': 'en' } }
                    );
                    const data = await res.json();
                    const addr = data.address || {};
                    const street = [addr.road, addr.suburb || addr.neighbourhood].filter(Boolean).join(', ');
                    const city = addr.city || addr.town || addr.village || addr.county || '';
                    const state = addr.state || '';
                    const pincode = (addr.postcode || '').replace(/\D/g, '').slice(0, 6);
                    setForm(prev => ({
                        ...prev,
                        street: street || prev.street,
                        city: city || prev.city,
                        state: state || prev.state,
                        pincode: pincode || prev.pincode,
                    }));
                    setErrors({ street: '', city: '', pincode: '' });
                    setGeoStatus('success');
                } catch {
                    setGeoStatus('error');
                }
            },
            () => setGeoStatus('error'),
            { enableHighAccuracy: true, timeout: 10000 }
        );
    };

    useEffect(() => {
        if (!currentUser) { setLoading(false); return; }
        const q = query(
            collection(db, 'users', currentUser.uid, 'addresses'),
            orderBy('createdAt', 'desc')
        );
        const unsubscribe = onSnapshot(q, (snap) => {
            setAddresses(snap.docs.map(d => ({ id: d.id, ...d.data() })));
            setLoading(false);
        }, (error) => {
            console.error('Error fetching addresses:', error);
            // Fallback: try without orderBy (avoids missing-index errors)
            const fallbackQ = collection(db, 'users', currentUser.uid, 'addresses');
            const fallbackUnsub = onSnapshot(fallbackQ, (snap) => {
                const docs = snap.docs.map(d => ({ id: d.id, ...d.data() }));
                docs.sort((a, b) => (b.createdAt || '').localeCompare(a.createdAt || ''));
                setAddresses(docs);
                setLoading(false);
            }, (err2) => {
                console.error('Fallback fetch also failed:', err2);
                setLoading(false);
            });
            return () => fallbackUnsub();
        });
        return () => unsubscribe();
    }, [currentUser]);

    useEffect(() => {
        if (showForm && firstInputRef.current) {
            setTimeout(() => firstInputRef.current?.focus(), 100);
        }
    }, [showForm]);

    const showToast = (msg, type = 'success') => {
        setToast({ msg, type });
        setTimeout(() => setToast({ msg: '', type: 'success' }), 3500);
    };

    const validate = () => {
        const errs = { ...emptyErrors };
        if (!form.street.trim()) errs.street = 'Street / Area is required.';
        if (!form.city.trim()) errs.city = 'City is required.';
        if (!form.pincode.trim()) errs.pincode = 'Pincode is required.';
        else if (form.pincode.length !== 6) errs.pincode = 'Enter a valid 6-digit pincode.';
        setErrors(errs);
        return !Object.values(errs).some(Boolean);
    };

    const handleOpenAdd = () => {
        setForm({ ...emptyAddress });
        setErrors({ ...emptyErrors });
        setEditMode(false);
        setEditId(null);
        setShowForm(true);
    };

    const handleOpenEdit = (addr) => {
        setForm({
            label: addr.label || 'home',
            flatNo: addr.flatNo || '',
            street: addr.street || '',
            landmark: addr.landmark || '',
            city: addr.city || '',
            state: addr.state || '',
            pincode: addr.pincode || '',
            isDefault: addr.isDefault || false,
        });
        setErrors({ ...emptyErrors });
        setEditMode(true);
        setEditId(addr.id);
        setShowForm(true);
    };

    const handleClose = () => {
        setShowForm(false);
        setEditMode(false);
        setEditId(null);
        setForm({ ...emptyAddress });
        setErrors({ ...emptyErrors });
    };

    const handleSave = async (e) => {
        e.preventDefault();
        if (!validate()) return;
        setSaving(true);
        try {
            const addressData = { ...form, updatedAt: new Date().toISOString() };
            if (form.isDefault) {
                for (const addr of addresses) {
                    if (addr.isDefault && addr.id !== editId) {
                        await updateDoc(doc(db, 'users', currentUser.uid, 'addresses', addr.id), { isDefault: false });
                    }
                }
            }
            if (editMode) {
                await updateDoc(doc(db, 'users', currentUser.uid, 'addresses', editId), addressData);
                showToast('Address updated successfully!');
            } else {
                await addDoc(collection(db, 'users', currentUser.uid, 'addresses'), {
                    ...addressData,
                    createdAt: new Date().toISOString(),
                });
                showToast('Address added successfully!');
            }
            handleClose();
        } catch (err) {
            showToast('Something went wrong. Try again.', 'error');
        }
        setSaving(false);
    };

    const handleDeleteConfirm = async () => {
        if (!confirmDeleteId) return;
        setDeletingId(confirmDeleteId);
        setConfirmDeleteId(null);
        try {
            await deleteDoc(doc(db, 'users', currentUser.uid, 'addresses', confirmDeleteId));
            showToast('Address deleted.');
        } catch (err) {
            showToast('Failed to delete. Try again.', 'error');
        }
        setDeletingId(null);
    };

    const handleSetDefault = async (id) => {
        try {
            for (const addr of addresses) {
                await updateDoc(doc(db, 'users', currentUser.uid, 'addresses', addr.id), {
                    isDefault: addr.id === id,
                });
            }
            showToast('Default address updated!');
        } catch (err) {
            showToast('Failed to update default.', 'error');
        }
    };

    const handleCopyAddress = (addr) => {
        const parts = [addr.flatNo, addr.street, addr.landmark ? `Near ${addr.landmark}` : '', addr.city, addr.state, addr.pincode].filter(Boolean);
        navigator.clipboard.writeText(parts.join(', ')).then(() => {
            setCopiedId(addr.id);
            setTimeout(() => setCopiedId(null), 2000);
        });
    };

    const getLabelData = (val) => LABEL_OPTIONS.find(l => l.value === val) || LABEL_OPTIONS[2];

    const filteredAddresses = filter === 'all' ? addresses : addresses.filter(a => a.label === filter);

    if (!currentUser) {
        return (
            <div className="sa-page">
                <div className="sa-login-prompt">
                    <div className="sa-login-icon">
                        <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                            <rect x="3" y="11" width="18" height="11" rx="2" /><path d="M7 11V7a5 5 0 0110 0v4" />
                        </svg>
                    </div>
                    <h2>Login Required</h2>
                    <p>Please log in to manage your saved addresses.</p>
                    <Link to="/login" className="sa-btn sa-btn-primary">Log In Now</Link>
                </div>
            </div>
        );
    }

    const defaultAddr = addresses.find(a => a.isDefault);

    return (
        <div className="sa-page">
            {/* Hero */}
            <div className="sa-hero">
                <div className="sa-hero-inner">
                    <div className="sa-hero-left">
                        <div className="sa-hero-icon">
                            <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                <path d="M21 10c0 7-9 13-9 13S3 17 3 10a9 9 0 0118 0z" /><circle cx="12" cy="10" r="3" />
                            </svg>
                        </div>
                        <div>
                            <h1 className="sa-hero-title">Saved Addresses</h1>
                            <p className="sa-hero-sub">
                                {addresses.length === 0
                                    ? 'No addresses saved yet'
                                    : `${addresses.length} address${addresses.length !== 1 ? 'es' : ''} saved`}
                            </p>
                        </div>
                    </div>
                    <button className="sa-btn sa-btn-primary" onClick={handleOpenAdd}>
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"><line x1="12" y1="5" x2="12" y2="19" /><line x1="5" y1="12" x2="19" y2="12" /></svg>
                        Add Address
                    </button>
                </div>
            </div>

            <div className="sa-content">
                {/* Toast */}
                {toast.msg && (
                    <div className={`sa-toast sa-toast-${toast.type}`}>
                        {toast.type === 'success'
                            ? <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12" /></svg>
                            : <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10" /><line x1="12" y1="8" x2="12" y2="12" /><line x1="12" y1="16" x2="12.01" y2="16" /></svg>
                        }
                        {toast.msg}
                    </div>
                )}

                {/* Default Banner */}
                {defaultAddr && (
                    <div className="sa-default-banner">
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <path d="M21 10c0 7-9 13-9 13S3 17 3 10a9 9 0 0118 0z" /><circle cx="12" cy="10" r="3" />
                        </svg>
                        <span>
                            <strong>Default:</strong>{' '}
                            {[defaultAddr.flatNo, defaultAddr.street, defaultAddr.city].filter(Boolean).join(', ')}
                        </span>
                    </div>
                )}

                {/* Filter Tabs */}
                {addresses.length > 0 && (
                    <div className="sa-filter-bar">
                        {FILTER_OPTIONS.map(f => (
                            <button
                                key={f.value}
                                className={`sa-filter-tab ${filter === f.value ? 'active' : ''}`}
                                onClick={() => setFilter(f.value)}
                            >
                                {f.label}
                                {f.value !== 'all' && (
                                    <span className="sa-filter-count">
                                        {addresses.filter(a => a.label === f.value).length}
                                    </span>
                                )}
                            </button>
                        ))}
                    </div>
                )}

                {/* Address List */}
                {loading ? (
                    <div className="sa-loading">
                        <div className="sa-spinner-ring"></div>
                        <p>Loading your addresses...</p>
                    </div>
                ) : addresses.length === 0 ? (
                    <div className="sa-empty">
                        <div className="sa-empty-illustration">
                            <svg width="80" height="80" viewBox="0 0 24 24" fill="none" strokeLinecap="round" strokeLinejoin="round">
                                <path d="M21 10c0 7-9 13-9 13S3 17 3 10a9 9 0 0118 0z" stroke="#FFD700" strokeWidth="1.5" />
                                <circle cx="12" cy="10" r="3" stroke="#FFD700" strokeWidth="1.5" />
                                <line x1="8" y1="22" x2="16" y2="22" stroke="#e5e5e5" strokeWidth="1.5" />
                            </svg>
                        </div>
                        <h3>No saved addresses</h3>
                        <p>Save your home, work, or any frequently visited locations for quicker booking.</p>
                        <button className="sa-btn sa-btn-primary" onClick={handleOpenAdd}>
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"><line x1="12" y1="5" x2="12" y2="19" /><line x1="5" y1="12" x2="19" y2="12" /></svg>
                            Add Your First Address
                        </button>
                    </div>
                ) : filteredAddresses.length === 0 ? (
                    <div className="sa-empty sa-empty-filter">
                        <p>No <strong>{filter}</strong> addresses saved.</p>
                        <button className="sa-filter-tab active" onClick={() => setFilter('all')}>Show All</button>
                    </div>
                ) : (
                    <div className="sa-address-grid">
                        {filteredAddresses.map((addr) => {
                            const labelData = getLabelData(addr.label);
                            return (
                                <div key={addr.id} className={`sa-card ${addr.isDefault ? 'sa-card-default' : ''} sa-label-${addr.label}`}>
                                    {/* Card Top Bar */}
                                    <div className="sa-card-topbar">
                                        <div className="sa-label-badge">
                                            <span className="sa-label-badge-icon">{labelData.icon}</span>
                                            <span className="sa-label-badge-text">{labelData.label}</span>
                                        </div>
                                        <div className="sa-card-actions">
                                            <button
                                                className="sa-icon-btn sa-copy-btn"
                                                onClick={() => handleCopyAddress(addr)}
                                                title="Copy address"
                                            >
                                                {copiedId === addr.id
                                                    ? <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12" /></svg>
                                                    : <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="9" y="9" width="13" height="13" rx="2" /><path d="M5 15H4a2 2 0 01-2-2V4a2 2 0 012-2h9a2 2 0 012 2v1" /></svg>
                                                }
                                            </button>
                                            <button
                                                className="sa-icon-btn sa-edit-btn"
                                                onClick={() => handleOpenEdit(addr)}
                                                title="Edit"
                                            >
                                                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M11 4H4a2 2 0 00-2 2v14a2 2 0 002 2h14a2 2 0 002-2v-7" /><path d="M18.5 2.5a2.121 2.121 0 013 3L12 15l-4 1 1-4 9.5-9.5z" /></svg>
                                            </button>
                                            <button
                                                className="sa-icon-btn sa-delete-btn"
                                                onClick={() => setConfirmDeleteId(addr.id)}
                                                disabled={deletingId === addr.id}
                                                title="Delete"
                                            >
                                                {deletingId === addr.id
                                                    ? <span className="sa-mini-spinner"></span>
                                                    : <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="3 6 5 6 21 6" /><path d="M19 6l-1 14a2 2 0 01-2 2H8a2 2 0 01-2-2L5 6" /><path d="M10 11v6M14 11v6" /><path d="M9 6V4a1 1 0 011-1h4a1 1 0 011 1v2" /></svg>
                                                }
                                            </button>
                                        </div>
                                    </div>

                                    {/* Default Tag */}
                                    {addr.isDefault && (
                                        <div className="sa-default-tag">
                                            <svg width="11" height="11" viewBox="0 0 24 24" fill="currentColor"><path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z" /></svg>
                                            Default
                                        </div>
                                    )}

                                    {/* Address Body */}
                                    <div className="sa-card-body">
                                        <div className="sa-addr-primary">
                                            {[addr.flatNo, addr.street].filter(Boolean).join(', ')}
                                        </div>
                                        {addr.landmark && (
                                            <div className="sa-addr-landmark">
                                                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10" /><line x1="12" y1="8" x2="12" y2="12" /><line x1="12" y1="16" x2="12.01" y2="16" /></svg>
                                                Near {addr.landmark}
                                            </div>
                                        )}
                                        <div className="sa-addr-secondary">
                                            {[addr.city, addr.state, addr.pincode].filter(Boolean).join(', ')}
                                        </div>
                                    </div>

                                    {/* Card Footer */}
                                    <div className="sa-card-footer">
                                        {addr.isDefault ? (
                                            <span className="sa-default-msg">
                                                <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12" /></svg>
                                                Used for bookings by default
                                            </span>
                                        ) : (
                                            <button className="sa-set-default-btn" onClick={() => handleSetDefault(addr.id)}>
                                                Set as Default
                                            </button>
                                        )}
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                )}
            </div>

            {/* Add / Edit Modal */}
            {showForm && (
                <div className="sa-modal-overlay" onClick={handleClose}>
                    <div className="sa-modal" onClick={e => e.stopPropagation()}>
                        <div className="sa-modal-header">
                            <div className="sa-modal-title-row">
                                <div className="sa-modal-header-icon">
                                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                        <path d="M21 10c0 7-9 13-9 13S3 17 3 10a9 9 0 0118 0z" /><circle cx="12" cy="10" r="3" />
                                    </svg>
                                </div>
                                <h3>{editMode ? 'Edit Address' : 'Add New Address'}</h3>
                            </div>
                            <button className="sa-modal-close" onClick={handleClose} aria-label="Close">
                                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"><line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" /></svg>
                            </button>
                        </div>

                        <form className="sa-modal-form" onSubmit={handleSave} noValidate>
                            {/* Fetch Location */}
                            <button
                                type="button"
                                className="sa-geo-btn"
                                onClick={handleFetchLocation}
                                disabled={geoStatus === 'loading'}
                            >
                                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 10c0 7-9 13-9 13S3 17 3 10a9 9 0 0118 0z" /><circle cx="12" cy="10" r="3" /></svg>
                                {geoStatus === 'loading' ? 'Detecting location…' :
                                    geoStatus === 'success' ? '✅ Location filled — review & edit below' :
                                        geoStatus === 'error' ? '❌ Could not detect — try again' :
                                            'Use My Current Location'}
                            </button>
                            {geoStatus === 'success' && (
                                <p className="sa-geo-hint">Fields below were auto-filled from GPS. You can edit them before saving.</p>
                            )}
                            {/* Label Selector */}
                            <div className="sa-field">
                                <label className="sa-field-label">Address Type</label>
                                <div className="sa-label-options">
                                    {LABEL_OPTIONS.map(option => (
                                        <button
                                            key={option.value}
                                            type="button"
                                            className={`sa-label-option ${form.label === option.value ? 'active' : ''}`}
                                            onClick={() => setForm({ ...form, label: option.value })}
                                        >
                                            <span className="sa-lo-icon">{option.icon}</span>
                                            <span>{option.label}</span>
                                        </button>
                                    ))}
                                </div>
                            </div>

                            <div className="sa-form-grid-2">
                                <div className="sa-field">
                                    <label className="sa-field-label">Flat / House No.</label>
                                    <input
                                        ref={firstInputRef}
                                        type="text"
                                        value={form.flatNo}
                                        onChange={e => setForm({ ...form, flatNo: e.target.value })}
                                        placeholder="e.g., Flat 4B, Block A"
                                        className="sa-input"
                                    />
                                </div>
                                <div className="sa-field">
                                    <label className="sa-field-label">
                                        Landmark <span className="sa-optional">(optional)</span>
                                    </label>
                                    <input
                                        type="text"
                                        value={form.landmark}
                                        onChange={e => setForm({ ...form, landmark: e.target.value })}
                                        placeholder="Near metro, park..."
                                        className="sa-input"
                                    />
                                </div>
                            </div>

                            <div className="sa-field">
                                <label className="sa-field-label">
                                    Street / Area <span className="sa-required">*</span>
                                </label>
                                <input
                                    type="text"
                                    value={form.street}
                                    onChange={e => { setForm({ ...form, street: e.target.value }); if (errors.street) setErrors({ ...errors, street: '' }); }}
                                    placeholder="Street, area, or locality"
                                    className={`sa-input ${errors.street ? 'sa-input-error' : ''}`}
                                />
                                {errors.street && <span className="sa-error-msg">{errors.street}</span>}
                            </div>

                            <div className="sa-form-grid-3">
                                <div className="sa-field">
                                    <label className="sa-field-label">City <span className="sa-required">*</span></label>
                                    <input
                                        type="text"
                                        value={form.city}
                                        onChange={e => { setForm({ ...form, city: e.target.value }); if (errors.city) setErrors({ ...errors, city: '' }); }}
                                        placeholder="City"
                                        className={`sa-input ${errors.city ? 'sa-input-error' : ''}`}
                                    />
                                    {errors.city && <span className="sa-error-msg">{errors.city}</span>}
                                </div>
                                <div className="sa-field">
                                    <label className="sa-field-label">State</label>
                                    <input
                                        type="text"
                                        value={form.state}
                                        onChange={e => setForm({ ...form, state: e.target.value })}
                                        placeholder="State"
                                        className="sa-input"
                                    />
                                </div>
                                <div className="sa-field">
                                    <label className="sa-field-label">Pincode <span className="sa-required">*</span></label>
                                    <input
                                        type="text"
                                        value={form.pincode}
                                        onChange={e => { setForm({ ...form, pincode: e.target.value.replace(/\D/g, '').slice(0, 6) }); if (errors.pincode) setErrors({ ...errors, pincode: '' }); }}
                                        placeholder="6-digit pin"
                                        className={`sa-input ${errors.pincode ? 'sa-input-error' : ''}`}
                                        maxLength={6}
                                    />
                                    {errors.pincode && <span className="sa-error-msg">{errors.pincode}</span>}
                                </div>
                            </div>

                            <label className="sa-checkbox-label">
                                <span className="sa-custom-checkbox">
                                    <input
                                        type="checkbox"
                                        checked={form.isDefault}
                                        onChange={e => setForm({ ...form, isDefault: e.target.checked })}
                                    />
                                    <span className="sa-checkmark">
                                        {form.isDefault && <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12" /></svg>}
                                    </span>
                                </span>
                                <span>Set as default address</span>
                            </label>

                            <div className="sa-modal-actions">
                                <button type="button" className="sa-btn sa-btn-ghost" onClick={handleClose}>Cancel</button>
                                <button type="submit" className="sa-btn sa-btn-primary" disabled={saving}>
                                    {saving
                                        ? <><span className="sa-mini-spinner sa-mini-spinner-dark"></span> Saving...</>
                                        : editMode ? 'Update Address' : 'Save Address'
                                    }
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            {/* Delete Confirmation Modal */}
            {confirmDeleteId && (
                <div className="sa-modal-overlay" onClick={() => setConfirmDeleteId(null)}>
                    <div className="sa-confirm-modal" onClick={e => e.stopPropagation()}>
                        <div className="sa-confirm-icon">
                            <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                                <polyline points="3 6 5 6 21 6" /><path d="M19 6l-1 14a2 2 0 01-2 2H8a2 2 0 01-2-2L5 6" />
                                <path d="M10 11v6M14 11v6" /><path d="M9 6V4a1 1 0 011-1h4a1 1 0 011 1v2" />
                            </svg>
                        </div>
                        <h4>Delete Address?</h4>
                        <p>This address will be permanently removed from your account.</p>
                        <div className="sa-confirm-actions">
                            <button className="sa-btn sa-btn-ghost" onClick={() => setConfirmDeleteId(null)}>Keep</button>
                            <button className="sa-btn sa-btn-danger" onClick={handleDeleteConfirm}>Delete</button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default SavedAddressesPage;
